// @flow strict

import * as React from 'react';
import classnames from 'classnames';
// eslint-disable-next-line css-modules/no-unused-class
import styles from './styles.scss';
import type { testIdPropType, AnalyticsType } from '../types';

export type LightVariants =
  | 'Default'
  | 'Black'
  | 'Accent'
  | 'Transparent'
  | 'Underline'
  | 'GhostInverse'
  | 'GhostInverseWhite';
export type DarkVariants =
  | 'Ghost'
  | 'White'
  | 'Citrus'
  | 'Argon'
  | 'DefaultInverse'
  | 'Bronze'
  | 'Silver'
  | 'Gold'
  | 'UnderlineDark'
  | 'UnderlineHigh';
export type Variants =
  | LightVariants
  | DarkVariants
  | 'Primary'
  | 'Link'
  | 'SubtleLink'
  | 'UnderlineGhost'
  | 'WhiteLink'
  | 'BlackLink'
  | 'BlackLinkThick';
export type Sizes = 'Medium' | 'Small' | 'Tiny';

type BasePropTypes = {|
  children: string | React.Element<'span'> | React.Element<any>[],
  analyticsEvent?: AnalyticsType,
  variant: Variants,
  size: Sizes,
  className?: string,
  itemProp?: string,
  id?: string,
  ...testIdPropType,
|};

type LinkPropTypes = {|
  ...BasePropTypes,
  type: 'link',
  href: string,
|};

type ButtonPropTypes = {|
  ...BasePropTypes,
  type: 'submit' | 'button',
  onClick: () => void,
|};

type PropTypes = LinkPropTypes | ButtonPropTypes;

const BaseButton = (props: PropTypes) => {
  const ref = React.useRef();
  const classes = classnames(
    styles.button,
    styles[`is${props.variant}`],
    styles[`is${props.size}`],
    props.className,
  );

  const attributes: { [string]: string } = {
    className: classes,
  };

  if (props['data-testid']) {
    attributes['data-testid'] = props['data-testid'];
  }

  if (props.itemProp) {
    attributes.itemProp = props.itemProp;
  }

  const onLinkClick = () => {
    if (props.analyticsEvent) {
      analytics.track(props.analyticsEvent.event, props.analyticsEvent.properties);
    }
  };

  if (props.type === 'link') {
    return (
      // $FlowFixMe
      <a id={props.id} href={props.href} ref={ref} {...attributes} onClick={onLinkClick}>
        {props.children}
      </a>
    );
  }

  const onClick = () => {
    if (props.analyticsEvent) {
      analytics.track(props.analyticsEvent.event, props.analyticsEvent.properties);
    }
    props.onClick();
  };

  return (
    // $FlowFixMe
    <button id={props.id} type={props.type} onClick={onClick} {...attributes}>
      {props.children}
    </button>
  );
};

const Link = (props: $Diff<LinkPropTypes, { type: 'link' }>) => (
  <BaseButton {...props} type="link" />
);

Link.defaultProps = {
  variant: 'Default',
  size: 'Medium',
};

const Button = (props: ButtonPropTypes) => <BaseButton {...props} />;
Button.defaultProps = {
  type: 'button',
  variant: 'Default',
  size: 'Medium',
};

export { Button, Link };
